import React from 'react';
import { useSelector } from 'react-redux';
import { isEditMode } from 'services/admin';
import { isMobileLayout } from 'services/device';
import { DynamicTranslationType, useEndUserInlineTextEditorTranslation, useEndUserTranslation } from 'hooks/use-translation';
import BlockSettings from './BlockSettings';
import ImageEditorModal from './ImageEditorModal';
import usePageBlock from 'hooks/use-page-block';
import { IImageAndText, IImageAndTextProps } from './utils';
import {
  BackgroundImage,
  Content,
  Description,
  Label,
  LayoutContainer,
  Title,
  StyledQuickEditOverlay,
  CTAEditorModal,
} from './styles';
import { getAdminBarWidth } from 'services/admin/selectors';
import { PREDEFINED_TOOLBAR_BUTTONS } from 'components/ui/RichTextEditorV2/utils';
import { CTAButton } from 'components/ui/CTAButton';

type EditableStringPath =
  | 'label.rawData'
  | 'title.rawData'
  | 'description.rawData'
  | 'cta.text'
  | 'cta.href'
  | 'cta.fontSize'
  | 'cta.variant'
  | 'cta.behavior'
  | 'cta.height.desktop'
  | 'cta.height.mobile';

const ImageAndText: React.FC<IImageAndTextProps> = ({
  onRemove,
  item,
  onEdit,
  pageId,
}) => {
  const isMobile = useSelector(isMobileLayout);
  const isEditing = useSelector(isEditMode);
  const adminBarWidth = useSelector(getAdminBarWidth);

  const { inlineTextEditorEndUserT } = useEndUserInlineTextEditorTranslation(isEditing);
  const { endUserT } = useEndUserTranslation();

  const shouldShowLocalizedVersion = !isEditing;
  const params = {
    blockId: item.arrayId,
    pageId,
  };

  const localizedLabel = inlineTextEditorEndUserT(DynamicTranslationType.imageAndTextBlockLabel, item.data.label.rawData, params);
  const localizedTitle = inlineTextEditorEndUserT(DynamicTranslationType.imageAndTextBlockTitle, item.data.title.rawData, params);
  const localizedDescription = inlineTextEditorEndUserT(DynamicTranslationType.imageAndTextBlockDescription, item.data.description.rawData, params);
  const localizedCtaText = endUserT([
    DynamicTranslationType.imageAndTextBlockCta,
    params,
  ]) || item.data.cta.text;

  const handleChangeData = React.useCallback((update: IImageAndText) => {
    onEdit({ ...item, data: update });
  }, [item, onEdit]);

  const {
    handleChange,
    handleCloseSettings,
    handleOpenSettings,
    isSettingsOpen,
  } = usePageBlock<EditableStringPath, IImageAndText>({
    item: item.data,
    onChange: handleChangeData,
  });

  const renderLabel = React.useCallback(() => {
    if (!item.data.label.show) {
      return null;
    }

    return (
      <Label
        blockTitleKey="ADMIN_LABEL"
        onChange={handleChange('label.rawData')}
        data={localizedLabel}
        alignment={item.data.alignment}
        maxLength={50}
        backgroundColor={item.data.background.color}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
        data-testid="imageAndTextLabel"
      />
    );
  }, [
    item.data.label.show,
    item.data.background.color,
    item.data.alignment,
    item.data.label.rawData,
    handleChange,
    localizedLabel,
  ]);

  const renderTitle = React.useCallback(() => {
    return (
      <Title
        blockTitleKey="ADMIN_LABEL_TITLE"
        onChange={handleChange('title.rawData')}
        data={localizedTitle}
        alignment={item.data.alignment}
        maxLength={250}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
        data-data-testid="imageAndTextTitle"
        htmlTag="h2"
      />
    );
  }, [item.data.alignment, item.data.title.rawData, handleChange, localizedTitle]);

  const renderDescription = React.useCallback(() => {
    if (!item.data.description.show) {
      return null;
    }

    return (
      <Description
        blockTitleKey="ADMIN_LABEL_DESCRIPTION"
        onChange={handleChange('description.rawData')}
        data={localizedDescription}
        alignment={item.data.alignment}
        maxLength={500}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.bodyEditor}
        data-testid="imageAndTextDescription"
      />
    );
  }, [
    item.data.description.show,
    item.data.alignment,
    item.data.description.rawData,
    handleChange,
    localizedDescription,
  ]);

  const renderCTA = React.useCallback(() => {
    if (!item.data.cta.show) {
      return null;
    }

    const cta = {
      ...item.data.cta,
      text: shouldShowLocalizedVersion ? localizedCtaText : item.data.cta.text,
    };

    return (
      <CTAEditorModal
        alignment={item.data.alignment}
        modalTitleKey="ADMIN_LABEL_CALL_TO_ACTION"
        blockTitleKey="ADMIN_LABEL_CALL_TO_ACTION"
        onChangeFontSize={handleChange('cta.fontSize')}
        onChangeVariant={handleChange('cta.variant')}
        onChangeBehavior={handleChange('cta.behavior')}
        onChangeText={handleChange('cta.text')}
        onChangeDesktopHeight={handleChange('cta.height.desktop')}
        onChangeMobileHeight={handleChange('cta.height.mobile')}
        cta={item.data.cta}
      >
        {(text) => (
          <CTAButton
            containerBackgroundColor={item.data.background.color}
            fontSize={cta.fontSize}
            variant={cta.variant}
            height={cta.height}
          >
            {text}
          </CTAButton>
        )}
      </CTAEditorModal>
    );
  }, [
    localizedCtaText,
    item.data.cta,
    item.data.background.color,
    item.data.alignment,
    isEditing,
    handleChange,
    pageId,
    shouldShowLocalizedVersion,
    adminBarWidth,
  ]);

  const backgroundImage = React.useMemo(() => {
    if (isMobile && item.data.background.image.mobile) {
      return item.data.background.image.mobile;
    }

    if (!isMobile && item.data.background.image.desktop) {
      return item.data.background.image.desktop;
    }

    return null;
  }, [
    isMobile,
    item.data.background.image.mobile,
    item.data.background.image.desktop,
  ]);

  return (
    <StyledQuickEditOverlay
      backgroundColor={item.data.background.color}
      onRemove={onRemove}
      titleKey="LABEL_IMAGE_AND_TEXT"
      onEdit={handleOpenSettings}
      testId={`image-and-text-${item.arrayId}`}
    >
      {item.data.background.image.show && backgroundImage && (
        <BackgroundImage
          src={backgroundImage}
          alt="Image and text Background Image"
        />
      )}
      <LayoutContainer layout={item.data.layout}>
        <Content>
          {renderLabel()}
          {renderTitle()}
          {renderDescription()}
          {renderCTA()}
        </Content>
        <ImageEditorModal item={item.data} onChange={handleChangeData} />
      </LayoutContainer>
      <BlockSettings
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        item={item.data}
        onChange={handleChangeData}
      />
    </StyledQuickEditOverlay>
  );
};

export default ImageAndText;
